(ns frontend.view.settings2.surveys.sub
    (:require
     [clojure.data :as data]
     [clojure.string :as string]
     [common.localization :as loc]
     [common.util :as cu]
     [frontend.logging :as logging]
     [frontend.state.current-session :as session]
     [potpuri.core :as potpuri]
     [re-frame.core :as rf]))

(rf/reg-sub
  ::tenant-questions
  (fn [db]
    (get db ::tenant-questions)))

(rf/reg-sub
  ::tenant-questions-ext-ids
  (fn [db]
    (get db ::tenant-questions-ext-ids)))

(rf/reg-sub
  ::selected-tab
  (fn [db]
    (or (get db ::selected-tab)
        :entity.type/survey-type)))

(rf/reg-sub
  ::selected-survey-type
  (fn [db]
    (get db ::selected-survey-type)))

(rf/reg-sub
  ::tenant-question-lookup
  :<-
  [::tenant-questions]
  (fn [tenant-questions _]
    (->> tenant-questions
         (map (fn [q]
                [(:db/id q) q]))
         (into {}))))

(rf/reg-sub
  ::question-registry
  :<-
  [::tenant-questions]
  (fn [tenant-questions]
    (->> tenant-questions
         (map (fn [q]
                (assoc
                  q
                  :label (or (get-in q [:survey-question/label-translations :ENG])
                             (->> q
                                  :survey-question/label-translations
                                  ((fn [l]
                                     (cond (map? l)
                                           (val (first l))
                                           :else
                                           (loc/tr :unknown-label [(pr-str l)]))))))
                  :languages (->> q
                                  :survey-question/label-translations
                                  (map key)))))
         (sort-by :label))))

(rf/reg-sub
  ::db
  (fn [db]
    db))

(rf/reg-sub
  ::template-editor-open?
  :<-
  [::survey-template]
  (fn [survey-template _]
    (number? (:db/id survey-template))))

(rf/reg-sub
  ::block-editor-open?
  :<-
  [::survey-block]
  (fn [survey-block _]
    (number? (:db/id survey-block))))

(rf/reg-sub
  ::survey-template
  (fn [db _]
    (get-in db [::survey-template])))

(rf/reg-sub
  ::survey-block
  (fn [db _]
    (get-in db [::survey-block])))

(rf/reg-sub
  ::saved-config
  :<-
  [::survey-template]
  (fn [survey-template _]
    (->> survey-template
         :survey-template/config)))

(rf/reg-sub
  ::block-config
  :<-
  [::survey-block]
  (fn [survey-block _]
    (->> survey-block
         :survey-block/config)))

(rf/reg-sub
  ::config-draft
  (fn [db _]
    (get db ::config-draft)))

(rf/reg-sub
  ::config-draft-changes?
  :<-
  [::config-draft]
  :<-
  [::saved-config]
  :<-
  [::saved-block-order]
  :<-
  [::block-order-draft]
  (fn [[config-draft saved-config saved-block-order block-order-draft] _]
    (let [[additions removals _] (data/diff config-draft saved-config)
          has-changes? (or (seq additions)
                           (seq removals))]
      (or has-changes?
          (and block-order-draft
               (not= saved-block-order block-order-draft))))))

(rf/reg-sub
  ::block-draft-changes?
  :<-
  [::config-draft]
  :<-
  [::block-config]
  (fn [[config-draft saved-config] _]
    (let [[additions removals _] (data/diff config-draft saved-config)
          has-changes? (or (seq additions)
                           (seq removals))]
      has-changes?)))

(rf/reg-sub
  ::block-missing-lang-versions?
  :<-
  [::config-draft]
  :<-
  [::block-config]
  :<-
  [::survey-block-languages]
  (fn [[config-draft saved-config languages] _]
    (let [combined (potpuri/deep-merge saved-config config-draft)
          languages-set (set languages)]
      (seq (concat (->> combined
                        :blocks
                        (cu/filter-by :block-type :survey-block/wysiwyg)
                        (keep (fn [block]
                                (let [[_extras missing _] (data/diff (->> (keys (:content block))
                                                                          (set))
                                                                     languages-set)]
                                  (some->> missing
                                           (remove nil?)
                                           (seq)))))
                        (seq))
                   (->> combined
                        :blocks
                        (keep (fn [block]
                                (let [block-label-keys (->> block
                                                            :label
                                                            (keys)
                                                            (set))
                                      [_extras missing _] (data/diff block-label-keys languages-set)
                                      missing (seq (remove nil? missing))]
                                  (when missing
                                    [(->> block
                                          :label
                                          (vals)
                                          (remove cu/blank-value?)
                                          (first))
                                     missing]))))
                        (seq)))))))

(rf/reg-sub
  ::template-languages
  :<-
  [::survey-template]
  (fn [template _]
    (:survey-template/languages template)))

(rf/reg-sub
  ::template-version
  :<-
  [::survey-template]
  (fn [template _]
    (:survey-template/schema-version template)))

(rf/reg-sub
  ::qualitative-questions?
  :<-
  [::survey-template]
  (fn [_template]
    true))

(rf/reg-sub
  ::template-blocks
  :<-
  [::survey-template]
  (fn [template _]
    (:survey-template/template-blocks template)))

(rf/reg-sub
  ::saved-block-order
  :<-
  [::survey-template]
  (fn [template _]
    (:survey-template/block-order template)))

(rf/reg-sub
  ::block-order-draft
  (fn [db _]
    (get db ::block-order-draft)))

(rf/reg-sub
  ::ordered-survey-blocks
  :<-
  [::saved-block-order]
  :<-
  [::block-order-draft]
  (fn [[saved-block-order block-order-draft] _]
    (let [order (vec (or block-order-draft saved-block-order))]
      (->> order
           (mapv (fn [survey-block-id]
                   (let [survey-block (session/get-entity survey-block-id)]
                     {:value (:db/id survey-block)
                      :label (:survey-block/label survey-block)})))))))

(rf/reg-sub
  ::keyed-survey-blocks
  :<-
  [::ordered-survey-blocks]
  (fn [blocks _]
    (->> blocks
         (mapv (fn [{:keys [value] :as opt}]
                 {:key  value
                  :item opt}))
         (distinct))))

(rf/reg-sub
  ::survey-block-languages
  :<-
  [::survey-block]
  (fn [template _]
    (:survey-block/languages template)))

(defn localize-wysiwyg-block
  [lang block]
  (assoc block
         :localized-label (get-in block [:label lang])
         :lang lang
         :localized-content (get-in block [:content lang])))

(defn localize-section-block
  [tenant-question-lookup lang block]
  (let [last-item-idx (dec (count (:content block)))]
    (assoc
      block
      :lang lang
      :localized-label (get-in block [:label lang])
      :localized-info (get-in block [:info lang])
      :content
      (->>
        block
        :content
        (map-indexed
          (fn [idx content-item]
            (case (:survey-item-type content-item)
              :survey-item/wysiwyg
              (assoc content-item
                     :lang lang
                     :first? (zero? idx)
                     :last? (= idx last-item-idx)
                     :localized-content (get-in content-item [:content lang]))
              :survey-item/question
              (let [question-item (get tenant-question-lookup (:survey-question-id content-item))]
                ;; merge inherits metadata from its first arg (question-item), so re-attach
                ;; the content-item's :temp-react-key to keep the list key stable.
                (with-meta
                  (merge question-item
                         content-item
                         {:lang             lang
                          :first?           (zero? idx)
                          :last?            (= idx last-item-idx)
                          :localized-info   (or (get-in content-item [:survey-question/info-translations lang])
                                                (get-in question-item [:survey-question/info-translations lang]))
                          :localized-label  (get-in question-item [:survey-question/label-translations lang])
                          :other-lang-label (->> question-item
                                                 :label
                                                 (keep val)
                                                 (remove cu/blank-value?)
                                                 (first))})
                  (meta content-item)))
              nil)))
        (vec)))))

(defn localize-table-block
  [tenant-question-lookup lang block]
  (js/console.log "Localize table block : " block)
  (-> block
      (assoc :lang lang
             :localized-label (get-in block [:label lang])
             :localized-info (get-in block [:info lang]))
      (update :content
              (fn [content]
                (->> content
                     (mapv (fn [content-item]
                             (let [survey-question-id (:survey-question-id content-item)
                                   question-item (get tenant-question-lookup survey-question-id)]
                               (merge question-item
                                      content-item
                                      {:lang                lang
                                       :id                  survey-question-id
                                       :localized-info      (or (get-in content-item [:survey-question/info-translations lang])
                                                                (get-in question-item [:survey-question/info-translations lang]))
                                       :localized-label     (get-in question-item [:survey-question/label-translations lang])
                                       :other-lang-label    (->> question-item
                                                                 :label
                                                                 (keep val)
                                                                 (remove cu/blank-value?)
                                                                 (first))
                                       :mandatoriness       :mandatory/always
                                       :cannot-skip?        true
                                       :show-mandatoriness? false
                                       :visibility          :visibility/always})))))))))

(rf/reg-sub
  ::template-config
  :<-
  [::tenant-question-lookup]
  :<-
  [::selected-language]
  :<-
  [::saved-config]
  :<-
  [::config-draft]
  (fn [[tenant-question-lookup lang saved-config config-draft] _]
    (when-let [config (or config-draft saved-config)]
      (assoc config
             :blocks
             (->> config
                  :blocks
                  (mapv
                    (fn [block]
                      (case (:block-type block)
                        :survey-block/wysiwyg
                        (localize-wysiwyg-block lang block)
                        :survey-block/section
                        (localize-section-block tenant-question-lookup lang block)
                        :survey-block/table-block
                        (localize-table-block tenant-question-lookup lang block)))))))))

(rf/reg-sub
  ::survey-block-config
  :<-
  [::tenant-question-lookup]
  :<-
  [::selected-language]
  :<-
  [::block-config]
  :<-
  [::config-draft]
  (fn [[tenant-question-lookup lang saved-config config-draft] _]
    (when-let [config (or config-draft saved-config)]
      (js/console.log " : CONFIG : " (type config) config)
      (try
        (assoc config
               :blocks
               (->> config
                    :blocks
                    (mapv
                      (fn [block]
                        (js/console.log "BLOCK : " block)
                        (case (:block-type block)
                          :survey-block/wysiwyg
                          (localize-wysiwyg-block lang block)
                          :survey-block/section
                          (localize-section-block tenant-question-lookup lang block)
                          :survey-block/table-block
                          (localize-table-block tenant-question-lookup lang block))))))
        (catch js/Object o
          (logging/error o))))))

(rf/reg-sub
  ::all-questions
  :<-
  [::template-config]
  (fn [config _]
    (->> config
         :blocks
         (cu/filter-by :block-type :survey-block/section)
         (mapcat :content)
         (filter :db/id))))

(rf/reg-sub
  ::all-question-options
  :<-
  [::all-questions]
  (fn [all-questions _]
    (->> all-questions
         (map (fn [q]
                {:id        (:db/id q)
                 :data-type (:survey-question/data-type q)
                 :choices   (:survey-question/options q)
                 :label     (get-in q [:survey-question/label-translations :ENG])}))
         (sort-by :label))))

(rf/reg-sub
  ::all-question-choices
  :<-
  [::all-question-options]
  (fn [questions]
    (->> questions
         (map (fn [{:keys [id choices]}]
                [id choices]))
         (into {}))))

(rf/reg-sub
  ::query-schema
  :<-
  [::all-question-options]
  (fn [all-options _]
    (->> all-options
         (map (fn [q]
                [(:id q) q]))
         (into {}))))

(rf/reg-sub
  ::question-selector-languages
  (fn [db [_]]
    (get db ::question-selector-languages)))

(rf/reg-sub
  ::selected-language
  :<-
  [::db]
  :<-
  [::template-languages]
  :<-
  [::survey-block-languages]
  (fn [[db languages block-languages] _]
    (or (get db ::selected-language)
        (first languages)
        (first block-languages))))

(rf/reg-sub
  ::reference-period
  :<-
  [::template-config]
  (fn [template-config _]
    (get template-config :reference-period)))

(rf/reg-sub
  ::show-questions-ext-ids?
  :<-
  [::template-config]
  (fn [template-config _]
    (get template-config :show-questions-ext-ids?)))

(rf/reg-sub
  ::multi-survey?
  :<-
  [::template-config]
  (fn [template-config _]
    (get template-config :multi-survey?)))

(rf/reg-sub
  ::hide-no-answer?
  :<-
  [::template-config]
  (fn [template-config _]
    (get template-config :hide-no-answer?)))

(rf/reg-sub
  ::excel-info-show?
  :<-
  [::template-config]
  (fn [template-config _]
    (get template-config :excel-info-show?)))

(rf/reg-sub
  ::show-previous-response-setting
  :<-
  [::template-config]
  (fn [template-config _]
    (get template-config :show-previous-response-setting)))

(rf/reg-sub
  ::excel-info-text
  :<-
  [::template-config]
  (fn [template-config [_ lang]]
    (get-in template-config [:excel-info-text lang])))

(rf/reg-sub
  ::mandatoriness-style
  :<-
  [::template-config]
  (fn [template-config]
    (get template-config :mandatoriness-style :mandatoriness-style/default)))

(rf/reg-sub
  ::lang-select-open?
  (fn [db _]
    (get db ::lang-select-open?)))

(rf/reg-sub
  ::question-mandatoriness-open?
  (fn [db [_ idx item-index]]
    (get-in db [::question-mandatoriness-open? idx item-index])))

(rf/reg-sub
  ::question-visibility-open?
  (fn [db [_ idx item-index]]
    (get-in db [::question-visibility-open? idx item-index])))

(rf/reg-sub
  ::question-mandatoriness-rule-open?
  (fn [db [_ idx item-index]]
    (get-in db [::question-mandatoriness-rule-open? idx item-index])))

(rf/reg-sub
  ::question-selector-open?
  (fn [db _]
    (get db ::question-selector-open?)))

(rf/reg-sub
  ::question-create-open?
  (fn [db _]
    (get db ::question-create-open?)))
(rf/reg-sub
  ::question-selector-path
  (fn [db _]
    (get db ::question-selector-path)))

(rf/reg-sub
  ::question-search-data-types
  (fn [db _]
    (seq (get db ::question-search-data-types))))

(rf/reg-sub
  ::question-search-str
  (fn [db _]
    (get db ::question-search-str)))

(rf/reg-sub
  ::selected-questions
  (fn [db _]
    (get db ::selected-questions)))

(rf/reg-sub
  ::tenant-question-search-results
  :<-
  [::db]
  :<-
  [::question-registry]
  :<-
  [::question-selector-path]
  :<-
  [::question-search-data-types]
  :<-
  [::question-search-str]
  (fn [[db question-registry questions-selector-path question-search-data-types search-str] _]
    (let [existing-questions (get-in db questions-selector-path)
          existing-ids (->> existing-questions
                            (map :survey-question-id)
                            (set))
          lower-case-search-str (cu/str-trim-lcase search-str)]
      (js/console.log "Search results : " question-search-data-types)
      (->> question-registry
           (remove (fn [{:keys [survey-question/data-type] :as q}]
                     (or (get existing-ids (:db/id q))
                         (when question-search-data-types
                           (js/console.log "Correct data type ? : " data-type " << " question-search-data-types)
                           (not (some #{data-type} question-search-data-types)))
                         (when-not (cu/blank-value? search-str)
                           (->> q
                                :survey-question/label-translations
                                (filter (fn [[_k label]]
                                          (string/includes? (cu/str-trim-lcase label) lower-case-search-str)))
                                (empty?))))))
           (vec)))))

(rf/reg-sub
  ::tenant-question-selections
  (fn [db _]
    (get db ::tenant-question-selections)))
